import { joinPath, resolvePath, dirname, basename } from '@shopify/cli-kit/node/path';
import { findFileWithExtension } from './file.js';
import { importVite } from './import-utils.js';
import { hasRemixConfigFile } from './remix-config.js';
import { renderWarning } from '@shopify/cli-kit/node/ui';

const REMIX_COMPILER_ERROR_MESSAGE = "Classic Remix Compiler projects are no longer supported, please upgrade to Vite by running 'npx shopify hydrogen setup vite'";
async function hasViteConfig(root) {
  const result = await findFileWithExtension(root, "vite.config");
  return !!result.filepath;
}
async function isViteProject(root) {
  const isVite = await hasViteConfig(root);
  if (isVite && await hasRemixConfigFile(root)) {
    renderWarning({
      headline: "Both Vite and Remix config files found.",
      body: "The remix.config.js file is not used in Vite projects. Please remove it to avoid conflicts."
    });
  }
  return isVite;
}
async function getViteConfig(root, ssrEntryFlag) {
  const vite = await importVite(root);
  const command = "build";
  const mode = process.env.NODE_ENV || "production";
  const maybeConfig = await vite.loadConfigFromFile(
    { command, mode, isSsrBuild: true },
    void 0,
    root
  );
  if (!maybeConfig || !maybeConfig.path) {
    throw new Error("No Vite config found");
  }
  const resolvedViteConfig = await vite.resolveConfig(
    { root, build: { ssr: true } },
    command,
    mode,
    mode
  );
  const { appDirectory, serverBuildFile, routes } = getReactRouterOrRemixConfigFromVite(resolvedViteConfig);
  const serverOutDir = resolvedViteConfig.build.outDir;
  const clientOutDir = serverOutDir.replace(/server$/, "client");
  const rollupOutput = resolvedViteConfig.build.rollupOptions.output;
  const { entryFileNames } = (Array.isArray(rollupOutput) ? rollupOutput[0] : rollupOutput) ?? {};
  const serverOutFile = joinPath(
    serverOutDir,
    typeof entryFileNames === "string" ? entryFileNames : serverBuildFile ?? "index.js"
  );
  const ssrEntry = ssrEntryFlag ?? resolvedViteConfig.build.ssr;
  const resolvedSsrEntry = resolvePath(
    resolvedViteConfig.root,
    typeof ssrEntry === "string" ? ssrEntry : "server"
  );
  return {
    clientOutDir,
    serverOutDir,
    serverOutFile,
    resolvedViteConfig,
    userViteConfig: maybeConfig.config,
    remixConfig: {
      routes: routes ?? {},
      appDirectory: appDirectory ?? joinPath(resolvedViteConfig.root, "app"),
      rootDirectory: resolvedViteConfig.root,
      serverEntryPoint: (await findFileWithExtension(
        dirname(resolvedSsrEntry),
        basename(resolvedSsrEntry)
      )).filepath || resolvedSsrEntry
    }
  };
}
function getReactRouterOrRemixConfigFromVite(viteConfig) {
  try {
    const reactRouterConfig = getReactRouterConfigFromVite(viteConfig);
    return reactRouterConfig;
  } catch (error) {
    const remixConfig = getRemixConfigFromVite(viteConfig);
    return remixConfig;
  }
}
function getReactRouterConfigFromVite(viteConfig) {
  if (!viteConfig.__reactRouterPluginContext) {
    throw new Error("Could not resolve React Router config");
  }
  const { appDirectory, serverBuildFile, routes } = viteConfig.__reactRouterPluginContext.reactRouterConfig;
  return {
    appDirectory,
    serverBuildFile,
    routes
  };
}
function getRemixConfigFromVite(viteConfig) {
  const { remixConfig } = findHydrogenPlugin(viteConfig)?.api?.getPluginOptions() ?? {};
  return remixConfig ? {
    appDirectory: remixConfig.appDirectory,
    serverBuildFile: remixConfig.serverBuildFile,
    routes: remixConfig.routes
  } : {};
}
function findPlugin(config, name) {
  return config.plugins.find((plugin) => plugin.name === name);
}
function findHydrogenPlugin(config) {
  return findPlugin(config, "hydrogen:main");
}
function findOxygenPlugin(config) {
  return findPlugin(config, "oxygen:main");
}

export { REMIX_COMPILER_ERROR_MESSAGE, findHydrogenPlugin, findOxygenPlugin, getViteConfig, hasViteConfig, isViteProject };
